import React from "react";
import {
  Box,
  Typography,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import { brand } from "../../../theme/themePrimitives";
import { AssignmentFormValues } from "../types";
import { Roles } from "../../../services/api/types/users/users";
import { useAuth } from "../../../context/AuthContext";

export type AssignmentActionMode = "update" | "create";

interface AssignmentActionToggleProps {
  value: AssignmentActionMode;
  onChange: (values: Partial<AssignmentFormValues>) => void;
}

const HELPER_TEXT: Record<AssignmentActionMode, string> = {
  update:
    "Existing assignment records will be updated with the new group and deadline settings.",
  create:
    "New training records will be created for the selected users and assignments.",
};

const OPTIONS: { value: AssignmentActionMode; label: string }[] = [
  { value: "update", label: "Update existing records" },
  { value: "create", label: "Create new training record" },
];

export const AssignmentActionToggle: React.FC<AssignmentActionToggleProps> = ({
  value,
  onChange,
}) => {
  const { auth } = useAuth();

  const isAdmin =
    auth.user?.roles?.includes(Roles.SYSTEM_ADMIN) ||
    auth.user?.roles?.includes(Roles.MAIN_ADMIN);

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: AssignmentActionMode | null,
  ) => {
    // Prevent deselection — one option must always be active
    if (newValue !== null) {
      onChange({ mode: newValue });
    }
  };

  return (
    <Paper
      sx={{
        p: "20px 24px",
        border: "1px solid #ACBDF5",
        borderRadius: "12px",
        boxShadow: "0px 0px 0px 4px #8A9AEF3D",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <ElectricBoltIcon sx={{ color: brand[600], fontSize: 20 }} />
        <Typography fontSize={16} fontWeight={600}>
          Assignment action
        </Typography>
      </Box>

      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={handleChange}
        disabled={!isAdmin}
        sx={{
          mb: 1.5,
          backgroundColor: "#F9FAFB",
          border: "1px solid #EAECF0",
          borderRadius: "12px",
          p: "4px",
          gap: 1,
          "& .MuiToggleButton-root": {
            border: "none",
            borderRadius: "6px",
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 600,
            color: "#6B7280",
            px: 2,
            py: 1,
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.6)",
            },
            "&.Mui-selected": {
              backgroundColor: "#FFFFFF",
              color: brand[600],
              fontWeight: 600,
              boxShadow: "0px 1px 2px 0px #1018280F",
              "&:hover": {
                backgroundColor: "#FFFFFF",
              },
            },
            "&.Mui-disabled": {
              border: "none",
            },
          },
        }}
      >
        {OPTIONS.map((option) => (
          <ToggleButton key={option.value} value={option.value}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Typography fontSize={12} color="#475467">
        {HELPER_TEXT[value]}
      </Typography>
    </Paper>
  );
};
